/**
 * Boswell production-engineering diagnostics (pure, no I/O).
 *
 * Consumes DealPackage `actuals.boswell` (monthly `recentMonths`, annual, and the
 * daily `recentDaily` series: gas Mcf/d, water Bbl, FTP psi) and turns it into
 * citeable facts for the desk: uptime / flowing-day fraction, WGR, FTP-vs-rate,
 * pre/post-acid windows, and a daily↔monthly reconciliation check.
 *
 * These are DIAGNOSTICS only — nothing here mutates package PV or `cfScale`.
 * The single Boswell volume judgment stays `boswellVolAdj` (see dca.js). Every
 * numeric getter is null-guarded (RETRO-46) so a partial/empty series never
 * throws in a formatter.
 */

/**
 * Documented coverage thresholds. `fitReady` is false when the history behind a
 * fit fails these — the desk should not treat a DCA fit as durable evidence.
 */
export const DIAGNOSTIC_THRESHOLDS = {
  // Daily series usable for uptime / FTP / WGR facts.
  minDailyDays: 45,
  // Flowing-day fraction below this = too much downtime to trust a daily rate.
  minFlowingFraction: 0.5,
  // Prefer >= this many monthly/annual points behind a decline fit.
  minHistoryPoints: 12,
  // A post-acid window shorter than this (days) cannot alone prove durability.
  shortPostAcidDays: 90,
  // Daily-avg vs monthly-avg gap above this (fraction) flags a reconciliation break.
  reconTolerance: 0.15,
};

function num(v) {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** Daily gas rate (Mcf/d) under any of the emitted field names. */
function dailyGas(r) {
  return num(r?.gas ?? r?.mcfpd ?? r?.gasMcf ?? r?.gasMcfd ?? null);
}

function dailyWater(r) {
  return num(r?.waterBbl ?? r?.water ?? r?.waterBbld ?? null);
}

function dailyFtp(r) {
  return num(r?.ftp ?? r?.ftpPsi ?? r?.tubingPsi ?? null);
}

function mean(arr) {
  const xs = arr.filter((x) => x != null && Number.isFinite(x));
  if (!xs.length) return null;
  return xs.reduce((s, x) => s + x, 0) / xs.length;
}

/** Uptime / flowing-day integrity from the daily gas series. */
export function uptimeStats(recentDaily) {
  const rows = Array.isArray(recentDaily) ? recentDaily : [];
  const gas = rows.map(dailyGas);
  const total = gas.length;
  const flowing = gas.filter((g) => g != null && g > 0).length;
  if (!total) {
    return { days: 0, flowingDays: 0, flowingFraction: null, avgFlowingRate: null, calendarAvgRate: null };
  }
  const flowingRates = gas.filter((g) => g != null && g > 0);
  return {
    days: total,
    flowingDays: flowing,
    flowingFraction: +(flowing / total).toFixed(3),
    // Rate on producing days only (what the well makes when it flows).
    avgFlowingRate: flowingRates.length ? +mean(flowingRates).toFixed(1) : null,
    // Downtime-diluted calendar rate (what a monthly average actually captures).
    calendarAvgRate: +(mean(gas) ?? 0).toFixed(1),
  };
}

/** Water-gas ratio in Bbl / MMcf across days with both gas and water. */
export function wgrStats(recentDaily) {
  const rows = Array.isArray(recentDaily) ? recentDaily : [];
  const ratios = [];
  let latest = null;
  for (const r of rows) {
    const g = dailyGas(r);
    const w = dailyWater(r);
    if (g != null && g > 0 && w != null) {
      const wgr = w / (g / 1000); // Bbl per MMcf
      ratios.push(wgr);
      latest = wgr;
    }
  }
  if (!ratios.length) return { hasWater: false, wgrBblPerMmcf: null, latestWgr: null, n: 0 };
  return {
    hasWater: true,
    wgrBblPerMmcf: +mean(ratios).toFixed(2),
    latestWgr: latest != null ? +latest.toFixed(2) : null,
    n: ratios.length,
  };
}

/** Flowing tubing pressure summary + a coarse FTP-vs-rate slope. */
export function ftpStats(recentDaily) {
  const rows = Array.isArray(recentDaily) ? recentDaily : [];
  const pairs = [];
  const ftps = [];
  let latest = null;
  for (const r of rows) {
    const p = dailyFtp(r);
    if (p == null) continue;
    ftps.push(p);
    latest = p;
    const g = dailyGas(r);
    if (g != null && g > 0) pairs.push({ ftp: p, gas: g });
  }
  if (!ftps.length) {
    return { hasFtp: false, avgFtp: null, latestFtp: null, minFtp: null, maxFtp: null, ftpVsRateSlope: null, n: 0 };
  }
  // Least-squares slope of FTP vs gas (psi per Mcf/d) — declining FTP with
  // holding rate is a durability tell; rising FTP with falling rate is not.
  let slope = null;
  if (pairs.length >= 3) {
    const n = pairs.length;
    let sX = 0;
    let sY = 0;
    let sXX = 0;
    let sXY = 0;
    for (const p of pairs) {
      sX += p.gas;
      sY += p.ftp;
      sXX += p.gas * p.gas;
      sXY += p.gas * p.ftp;
    }
    const den = n * sXX - sX * sX;
    if (Math.abs(den) > 1e-9) slope = +((n * sXY - sX * sY) / den).toFixed(4);
  }
  return {
    hasFtp: true,
    avgFtp: +(mean(ftps) ?? 0).toFixed(1),
    latestFtp: latest != null ? +latest.toFixed(1) : null,
    minFtp: +Math.min(...ftps).toFixed(1),
    maxFtp: +Math.max(...ftps).toFixed(1),
    ftpVsRateSlope: slope,
    n: ftps.length,
  };
}

/**
 * Acid step decomposition — FACTS the desk can cite, kept separate from the
 * scenario judgment. Prefers pre-computed `stats.acid`, falls back to the
 * monthly series around a provided/observed acid month.
 */
export function acidWindowStats({ recentDaily, recentMonths, acid } = {}) {
  const months = (Array.isArray(recentMonths) ? recentMonths : [])
    .map((m) => ({ month: m?.month, mcfpd: num(m?.mcfpd) }))
    .filter((m) => m.mcfpd != null);
  const daily = Array.isArray(recentDaily) ? recentDaily : [];

  const preAcidBaseline = num(acid?.preAcidAvgMcfpd);
  const last30 = num(acid?.last30AvgMcfpd);

  // Observed post-acid window length from the daily series (days after peak).
  let postAcidDays = null;
  if (daily.length) {
    const gas = daily.map(dailyGas);
    let peakIdx = -1;
    let peakVal = -Infinity;
    gas.forEach((g, i) => {
      if (g != null && g > peakVal) {
        peakVal = g;
        peakIdx = i;
      }
    });
    if (peakIdx >= 0) postAcidDays = gas.length - peakIdx;
  }

  // Peak → subsequent trough from the monthly series (used for fade anchoring).
  let peakMonthIdx = -1;
  let peakMonthVal = -Infinity;
  months.forEach((m, i) => {
    if (m.mcfpd > peakMonthVal) {
      peakMonthVal = m.mcfpd;
      peakMonthIdx = i;
    }
  });
  let observedFadeMonths = null;
  let monthlyTrough = null;
  if (peakMonthIdx >= 0 && peakMonthIdx < months.length - 1) {
    const after = months.slice(peakMonthIdx);
    let troughIdx = 0;
    after.forEach((m, i) => {
      if (m.mcfpd < after[troughIdx].mcfpd) troughIdx = i;
    });
    if (troughIdx > 0) {
      observedFadeMonths = troughIdx;
      monthlyTrough = after[troughIdx].mcfpd;
    }
  }

  const peak = peakMonthVal > -Infinity ? peakMonthVal : (last30 ?? null);
  const upliftMcfpd =
    preAcidBaseline != null && peak != null ? +(peak - preAcidBaseline).toFixed(1) : null;
  const upliftPct =
    preAcidBaseline != null && preAcidBaseline > 0 && peak != null
      ? +(((peak - preAcidBaseline) / preAcidBaseline) * 100).toFixed(1)
      : num(acid?.stepPct);

  return {
    hasAcidFacts: preAcidBaseline != null || last30 != null || acid != null,
    preAcidBaselineMcfpd: preAcidBaseline,
    postAcidPeakMcfpd: peak != null ? +peak.toFixed(1) : null,
    last30AvgMcfpd: last30,
    upliftMcfpd,
    upliftPct,
    postAcidDays,
    observedFadeMonths,
    monthlyTroughMcfpd: monthlyTrough != null ? +monthlyTrough.toFixed(1) : null,
    // A FACT-vs-JUDGMENT flag the UI mirrors: short windows can't prove durability.
    shortWindow:
      postAcidDays != null ? postAcidDays < DIAGNOSTIC_THRESHOLDS.shortPostAcidDays : null,
  };
}

/**
 * Daily↔monthly reconciliation: for months covered by the daily series, compare
 * the daily flowing/calendar average to the reported monthly average.
 */
export function reconcileDailyMonthly({ recentDaily, recentMonths } = {}) {
  const daily = Array.isArray(recentDaily) ? recentDaily : [];
  const months = Array.isArray(recentMonths) ? recentMonths : [];
  if (!daily.length || !months.length) {
    return { covered: 0, checks: [], maxDeltaPct: null, withinTolerance: null };
  }
  // Bucket daily gas by YYYY-MM.
  const byMonth = new Map();
  for (const r of daily) {
    const d = String(r?.date ?? r?.day ?? '');
    const ym = d.slice(0, 7);
    const g = dailyGas(r);
    if (!ym || g == null) continue;
    if (!byMonth.has(ym)) byMonth.set(ym, []);
    byMonth.get(ym).push(g);
  }
  const checks = [];
  for (const m of months) {
    const ym = String(m?.month ?? '').slice(0, 7);
    const reported = num(m?.mcfpd);
    const bucket = byMonth.get(ym);
    if (!ym || reported == null || !bucket?.length) continue;
    const dailyAvg = mean(bucket);
    if (dailyAvg == null || reported === 0) continue;
    const deltaPct = +(((dailyAvg - reported) / reported) * 100).toFixed(1);
    checks.push({ month: ym, reportedMcfpd: +reported.toFixed(1), dailyAvgMcfpd: +dailyAvg.toFixed(1), deltaPct });
  }
  if (!checks.length) return { covered: 0, checks: [], maxDeltaPct: null, withinTolerance: null };
  const maxDeltaPct = Math.max(...checks.map((c) => Math.abs(c.deltaPct)));
  return {
    covered: checks.length,
    checks,
    maxDeltaPct: +maxDeltaPct.toFixed(1),
    withinTolerance: maxDeltaPct / 100 <= DIAGNOSTIC_THRESHOLDS.reconTolerance,
  };
}

/**
 * Roll it all up into one diagnostic snapshot for the UI / freeze / capital pack.
 * `fitReady` is a documented gate: it is false when neither monthly/annual
 * history nor daily coverage clears the thresholds above.
 */
export function buildProductionDiagnostics({ actuals } = {}) {
  if (!actuals) return null;
  const recentDaily = actuals.recentDaily || [];
  const recentMonths = actuals.recentMonths || [];
  const annual = actuals.annual || [];
  const stats = actuals.stats || {};

  const uptime = uptimeStats(recentDaily);
  const wgr = wgrStats(recentDaily);
  const ftp = ftpStats(recentDaily);
  const acid = acidWindowStats({ recentDaily, recentMonths, acid: stats.acid });
  const recon = reconcileDailyMonthly({ recentDaily, recentMonths });

  const historyPoints = recentMonths.length + annual.length;
  const reasons = [];
  const enoughHistory = historyPoints >= DIAGNOSTIC_THRESHOLDS.minHistoryPoints;
  if (!enoughHistory) {
    reasons.push(`only ${historyPoints} history points (< ${DIAGNOSTIC_THRESHOLDS.minHistoryPoints})`);
  }
  // Daily coverage is required for fitReady — monthly/annual alone must not
  // green-light a PE fit when recentDaily is missing or empty (Bugbot #71).
  let dailyOk = false;
  if (uptime.days <= 0) {
    reasons.push('no recentDaily series');
  } else {
    dailyOk = true;
    if (uptime.days < DIAGNOSTIC_THRESHOLDS.minDailyDays) {
      dailyOk = false;
      reasons.push(`daily series ${uptime.days}d (< ${DIAGNOSTIC_THRESHOLDS.minDailyDays})`);
    }
    if (
      uptime.flowingFraction != null &&
      uptime.flowingFraction < DIAGNOSTIC_THRESHOLDS.minFlowingFraction
    ) {
      dailyOk = false;
      reasons.push(`flowing fraction ${uptime.flowingFraction} (< ${DIAGNOSTIC_THRESHOLDS.minFlowingFraction})`);
    }
  }
  if (acid.shortWindow) reasons.push('post-acid window < 90d — cannot prove durability alone');

  const fitReady = enoughHistory && dailyOk;

  return {
    hasDaily: uptime.days > 0,
    dailyDays: uptime.days,
    historyPoints,
    uptime,
    wgr,
    ftp,
    acid,
    recon,
    fitReady,
    fitReadyReasons: reasons,
    thresholds: DIAGNOSTIC_THRESHOLDS,
  };
}
